import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  BatchGridBulkEditResponse,
  BatchGridBulkOutcome,
  BatchGridBulkSummary,
} from '@tcvs/shared';
import { OwnershipLabel, type OwnershipLabels } from '../consignment/OwnershipLabel';
import type {
  CardIdentity,
  ChecklistParallelDefinition,
  ChecklistSubsetDefinition,
  ReviewChecklistProduct,
} from '../shared/types';
import { FilterableSelect } from '../shared/FilterableSelect';
import { useCatalogApi, type CatalogListCardRecord } from '../catalog/catalog-api';
import { usdInputToCents } from '../catalog/card-research';
import { parallelDefinitionLabel } from '../catalog/checklist-parallel-selection';
import {
  ChecklistSelectionChanges,
  validChecklistIdentityProposal,
} from './ChecklistSelectionChanges';
const digest = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
export function validGridSourcePreview(
  value: BatchGridBulkEditResponse,
  batchId: string,
  field: string,
  ids: readonly string[],
): boolean {
  if (!value || typeof value !== 'object') return false;
  return (
    value.previewVersion === 2 &&
    value.preview === true &&
    value.batchId === batchId &&
    value.field === field &&
    digest(value.contextPreviewDigest) &&
    Array.isArray(value.outcomes) &&
    value.outcomes.length === ids.length &&
    new Set(value.outcomes.map((row) => row?.cardId)).size === ids.length &&
    value.outcomes.every(
      (row) =>
        row &&
        ids.includes(row.cardId) &&
        ['UPDATED', 'UNCHANGED', 'UNRESOLVED', 'FAILED', 'UNKNOWN'].includes(row.status) &&
        (row.identityProposal === undefined ||
          validChecklistIdentityProposal(row.identityProposal)) &&
        (!(row.checklistAssigned === true && ['UPDATED', 'UNCHANGED'].includes(row.status)) ||
          (digest(row.proposalDigest) && validChecklistIdentityProposal(row.identityProposal))),
    )
  );
}

type GridPreset = 'IDENTIFICATION' | 'LISTING' | 'INVENTORY' | 'ALL';
type GridSort = 'POSITION' | 'CARD' | 'NUMBER' | 'NAME' | 'PRICE';
type EditableField =
  | 'SET'
  | 'SUBSET'
  | 'PARALLEL'
  | 'PRICE'
  | 'ROOKIE'
  | 'SERIAL_NUMBERED'
  | 'AUTOGRAPH'
  | 'MEMORABILIA';

interface BatchIdentificationGridProps {
  ownership?: OwnershipLabels;
  batchId: string;
  cards: CatalogListCardRecord[];
  checklists: ReviewChecklistProduct[];
  batchChecklistId: string | null;
  checklistSearchLoading?: boolean;
  checklistSearchError?: string | null;
  checklistSearchHasMore?: boolean;
  onChecklistLoadMore?: () => void;
  onChecklistRetry?: () => void;
  onChecklistSearch?: (query: string) => void;
  onOpenCard: (cardId: string) => void;
  onUpdated: () => Promise<void>;
}

function identityForGrid(card: CatalogListCardRecord): CardIdentity | undefined {
  return (
    card.identityReviewDraft?.identity ??
    card.identity ??
    card.checklistSuggestion ??
    card.csvSuggestion ??
    card.ocrSuggestion ??
    (card.suggestion?.identification.status === 'known'
      ? card.suggestion.identification.identity
      : undefined)
  );
}

function reviewState(card: CatalogListCardRecord): string {
  if (card.retiredAt !== undefined) return 'Retired';
  if (
    card.reviewChecklistSelection?.requiresReview &&
    card.reviewChecklistSelection.reason === 'parallel_selection_required'
  )
    return 'Needs parallel';
  if (card.reviewChecklistSelection?.requiresReview)
    return card.reviewChecklistSelection.identityResolution === 'UNRESOLVED'
      ? 'Pending identification'
      : 'Review required';
  if (card.identityReviewDraft?.importedFrom !== undefined) return 'Imported draft';
  if (card.identityReviewDraft !== undefined) return 'Draft updated';
  return card.identityStatus === 'IDENTIFIED' ? 'Identified' : 'Needs review';
}

function assignedSetLabel(
  card: CatalogListCardRecord,
  checklists: readonly ReviewChecklistProduct[],
): string {
  const selection = card.reviewChecklistSelection;
  if (selection?.requiresReview || selection?.identityResolution) {
    return (
      selection.product?.trim() ||
      checklists.find((item) => item.checklistId === selection.checklistId)?.product.product ||
      card.identityReviewDraft?.identity.set ||
      'Assigned checklist'
    );
  }
  return identityForGrid(card)?.set || '—';
}

function needsAttention(item: BatchGridBulkOutcome): boolean {
  return (
    item.status === 'UNRESOLVED' ||
    item.status === 'FAILED' ||
    item.status === 'UNKNOWN' ||
    item.identityResolution === 'UNRESOLVED'
  );
}

function assignmentSummary(summary: BatchGridBulkSummary, preview = false): string {
  return `${summary.assigned ?? 0} ${preview ? 'will be assigned' : 'assigned'}, ${summary.matched ?? 0} exact matches, ${summary.needsReview ?? 0} pending identification, ${summary.unchanged} unchanged, ${summary.unresolved} not assigned, ${summary.failed} failed.`;
}

function commonDraftSummary(result: BatchGridBulkEditResponse): string | null {
  const pending = result.outcomes.filter(
    (item) => item.reason === 'parallel_selection_required',
  ).length;
  if (result.summary.assigned !== undefined || result.field !== 'SUBSET' || pending === 0)
    return null;
  const summary = result.summary;
  return `${summary.updated} drafts ${result.preview ? 'will update' : 'updated'}, ${pending} pending parallel ${pending === 1 ? 'choice' : 'choices'}, ${summary.unchanged} unchanged, ${summary.unresolved} unresolved, ${summary.failed} failed.`;
}

function dollars(card: CatalogListCardRecord): string {
  return card.listingPrice === undefined
    ? '—'
    : `$${(card.listingPrice.amountMinor / 100).toFixed(2)}`;
}

function boolLabel(value: boolean | undefined): string {
  return value === true ? 'Yes' : value === false ? 'No' : '—';
}

function serialLabel(identity: CardIdentity | undefined): string {
  if (
    identity?.serialNumbered !== true &&
    identity?.serialTo === undefined &&
    identity?.serialNumber === undefined
  ) {
    return '—';
  }
  if (identity.serialTo === undefined) return 'Yes';
  return identity.serialNumber === undefined
    ? `/${identity.serialTo}`
    : `${identity.serialNumber}/${identity.serialTo}`;
}

function fieldLabel(field: EditableField): string {
  return {
    SET: 'Set',
    SUBSET: 'Subset',
    PARALLEL: 'Parallel',
    PRICE: 'Price',
    ROOKIE: 'Rookie',
    SERIAL_NUMBERED: 'Serial numbered',
    AUTOGRAPH: 'Autograph',
    MEMORABILIA: 'Memorabilia',
  }[field];
}

export function inferBatchGridChecklistId(
  batchChecklistId: string | null,
  cards: readonly CatalogListCardRecord[],
  checklists: readonly ReviewChecklistProduct[],
): string | null {
  if (batchChecklistId !== null && batchChecklistId.trim() !== '') return batchChecklistId;
  const knownIds = new Set(checklists.map((item) => item.checklistId));
  const cardIds = new Set(
    cards.flatMap((card) => {
      const checklistId =
        card.reviewChecklistSelection?.checklistId ?? card.checklistSourceLineage?.checklistId;
      return checklistId === undefined || !knownIds.has(checklistId) ? [] : [checklistId];
    }),
  );
  return cardIds.size === 1 ? ([...cardIds][0] ?? null) : null;
}

function uniqueSubsetOptions(definitions: readonly ChecklistSubsetDefinition[]) {
  return [
    ...new Map(definitions.map((definition) => [definition.definitionId, definition])).values(),
  ]
    .filter((definition) => definition.definitionId && definition.name.trim())
    .sort((left, right) => left.name.localeCompare(right.name, undefined, { numeric: true }))
    .map((definition) => ({
      value: definition.definitionId,
      label: `${definition.name}${definition.serialTo === undefined ? '' : ` · /${definition.serialTo}`}${definition.notes ? ` · ${definition.notes}` : ''}`,
      searchText: `${definition.source.subsetId} ${definition.printRunText ?? ''}`,
    }));
}

function uniqueParallelOptions(definitions: readonly ChecklistParallelDefinition[]) {
  return [
    ...new Map(definitions.map((definition) => [definition.definitionId, definition])).values(),
  ]
    .filter((definition) => definition.definitionId && definition.name.trim())
    .sort((left, right) => left.name.localeCompare(right.name, undefined, { numeric: true }))
    .map((definition) => ({
      value: definition.definitionId,
      label: `${parallelDefinitionLabel(definition)}${definition.applicability.subset ? ` · ${definition.applicability.subset} only` : ''}`,
      searchText: `${definition.source.subsetId} ${definition.printRunText ?? ''} ${definition.notes ?? ''}`,
    }));
}

export function batchGridOutcomeReason(reason: string | undefined): string {
  return (
    {
      checklist_item_not_found: 'Card number was not found in the selected checklist.',
      checklist_item_not_found_in_subset: 'Card number is not present in the selected subset.',
      checklist_item_ambiguous: 'More than one exact checklist row still matches this card.',
      card_number_required:
        'Assigned choices saved. Enter the printed card number to identify this card.',
      subset_definition_ambiguous_or_missing: 'Choose a specific imported subset definition.',
      subset_definition_changed:
        'This subset definition changed. Reload the choices and preview again.',
      parallel_ambiguous_or_incompatible:
        'Choose a specific parallel offered for the assigned subset.',
      subset_selection_required: 'Choose a subset before assigning a parallel.',
      parallel_selection_required: 'Choose the card’s parallel to finish identification.',
      parallel_not_applicable: "Selected parallel is not offered for this card's subset.",
      parallel_ambiguous: 'More than one parallel definition still matches this card.',
      source_lineage_missing: 'The exact checklist source record is missing.',
      identity_missing: 'Card identity is not populated yet.',
      listed_card_review_locked: 'This card is listed or sold and was not changed.',
      card_not_reviewable: 'This card is no longer available for review and was not changed.',
      ConditionalCheckFailedException:
        'This card changed during the update. Refresh and preview again.',
      TransactionCanceledException:
        'The card or its listing review changed during the update. Refresh and preview again.',
      write_failed: 'The update could not be saved. Refresh and try again.',
      approved_listing_review_locked:
        'This card has an approved listing review and was not changed.',
    }[reason ?? ''] ??
    reason ??
    'Bulk update failed.'
  );
}

function EditableHeader({
  field,
  onEdit,
}: {
  field: EditableField;
  onEdit: (field: EditableField) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className="tcvs-grid-header-edit"
      title={`Bulk edit ${fieldLabel(field)}`}
      onClick={() => onEdit(field)}
    >
      {fieldLabel(field)} <span aria-hidden="true">Edit</span>
    </button>
  );
}

export function BatchIdentificationGrid({
  ownership = {},
  batchId,
  cards,
  checklists,
  batchChecklistId,
  checklistSearchLoading = false,
  checklistSearchError,
  checklistSearchHasMore,
  onChecklistLoadMore,
  onChecklistRetry,
  onChecklistSearch,
  onOpenCard,
  onUpdated,
}: BatchIdentificationGridProps): JSX.Element {
  const catalog = useCatalogApi();
  const [preset, setPreset] = useState<GridPreset>('IDENTIFICATION');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<GridSort>('POSITION');
  const [descending, setDescending] = useState(false);
  const [editing, setEditing] = useState<EditableField | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editBoolean, setEditBoolean] = useState(true);
  const [editorChecklistId, setEditorChecklistId] = useState('');
  const [subsetDefinitions, setSubsetDefinitions] = useState<ChecklistSubsetDefinition[]>([]);
  const [parallelDefinitions, setParallelDefinitions] = useState<ChecklistParallelDefinition[]>([]);
  const [sourceOptionsBusy, setSourceOptionsBusy] = useState(false);
  const [identityChangesAccepted, setIdentityChangesAccepted] = useState(false);
  const [priceMode, setPriceMode] = useState<'REPLACE' | 'FILL_BLANKS'>('REPLACE');
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof catalog.bulkEditBatch>> | null>(
    null,
  );
  const [busy, setBusy] = useState<'PREVIEW' | 'APPLY' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [appliedResult, setAppliedResult] = useState<BatchGridBulkEditResponse | null>(null);
  const requestSequence = useRef(0);
  useEffect(() => {
    setAppliedResult(null);
    setNotice(null);
    setError(null);
    setPreview(null);
    setEditing(null);
    setBusy(null);
    return () => {
      requestSequence.current += 1;
    };
  }, [batchId]);
  const invalidatePreview = () => {
    requestSequence.current += 1;
    setPreview(null);
    setIdentityChangesAccepted(false);
  };

  const rows = useMemo(() => {
    const folded = query.trim().toLocaleLowerCase('en-US');
    const filtered = cards.filter((card) => {
      if (folded === '') return true;
      const identity = identityForGrid(card);
      return [
        card.cardId,
        card.shortCode,
        card.sku,
        assignedSetLabel(card, checklists),
        identity?.subset,
        identity?.variation,
        identity?.number,
        identity?.name,
        identity?.team,
      ].some((value) => value?.toLocaleLowerCase('en-US').includes(folded));
    });
    const direction = descending ? -1 : 1;
    return filtered.sort((left, right) => {
      if (sort === 'POSITION') {
        return ((left.currentBatchPosition ?? 0) - (right.currentBatchPosition ?? 0)) * direction;
      }
      const leftIdentity = identityForGrid(left);
      const rightIdentity = identityForGrid(right);
      const leftValue =
        sort === 'NUMBER'
          ? leftIdentity?.number
          : sort === 'NAME'
            ? leftIdentity?.name
            : sort === 'PRICE'
              ? String(left.listingPrice?.amountMinor ?? -1).padStart(12, '0')
              : (left.shortCode ?? left.cardId);
      const rightValue =
        sort === 'NUMBER'
          ? rightIdentity?.number
          : sort === 'NAME'
            ? rightIdentity?.name
            : sort === 'PRICE'
              ? String(right.listingPrice?.amountMinor ?? -1).padStart(12, '0')
              : (right.shortCode ?? right.cardId);
      return (
        (leftValue ?? '').localeCompare(rightValue ?? '', undefined, { numeric: true }) * direction
      );
    });
  }, [cards, checklists, descending, query, sort]);

  const show = (column: 'IDENTITY' | 'LISTING' | 'INVENTORY') =>
    preset === 'ALL' ||
    (preset === 'IDENTIFICATION' && column === 'IDENTITY') ||
    (preset === 'LISTING' && (column === 'IDENTITY' || column === 'LISTING')) ||
    (preset === 'INVENTORY' && column === 'INVENTORY');
  const showPrice = preset !== 'INVENTORY';
  const inferredChecklistId = useMemo(
    () => inferBatchGridChecklistId(batchChecklistId, cards, checklists),
    [batchChecklistId, cards, checklists],
  );
  const subsetOptions = useMemo(() => uniqueSubsetOptions(subsetDefinitions), [subsetDefinitions]);
  const parallelOptions = useMemo(
    () => [
      { value: '__none__', label: 'No parallel / Base version (preview checks every card)' },
      ...uniqueParallelOptions(parallelDefinitions),
    ],
    [parallelDefinitions],
  );

  useEffect(() => {
    if (
      (editing !== 'SUBSET' && editing !== 'PARALLEL') ||
      editorChecklistId === '' ||
      cards[0] === undefined
    ) {
      setSourceOptionsBusy(false);
      setSubsetDefinitions([]);
      setParallelDefinitions([]);
      return;
    }
    let current = true;
    setSourceOptionsBusy(true);
    setError(null);
    void catalog
      .checklistResolve(cards[0].cardId, {
        checklistId: editorChecklistId,
        browseSubsets: true,
        tentativeChecklist: true,
      })
      .then((result) => {
        if (!current) return;
        if (!result.ok) {
          setError(result.message ?? result.error);
          setSubsetDefinitions([]);
          setParallelDefinitions([]);
          return;
        }
        setSubsetDefinitions(result.data.subsetDefinitions ?? []);
        setParallelDefinitions(result.data.parallelDefinitions ?? []);
      })
      .catch((caught: unknown) => {
        if (!current) return;
        setError(
          caught instanceof Error ? caught.message : 'Checklist options could not be loaded.',
        );
        setSubsetDefinitions([]);
        setParallelDefinitions([]);
      })
      .finally(() => {
        if (current) setSourceOptionsBusy(false);
      });
    return () => {
      current = false;
    };
  }, [cards, catalog, editing, editorChecklistId]);

  const openEditor = (field: EditableField) => {
    if (busy === 'APPLY') return;
    setBusy(null);
    setEditing(field);
    setEditValue(field === 'SET' ? (batchChecklistId ?? '') : '');
    setEditorChecklistId(
      field === 'SUBSET' || field === 'PARALLEL' ? (inferredChecklistId ?? '') : '',
    );
    setEditBoolean(true);
    setPriceMode('REPLACE');
    invalidatePreview();
    setError(null);
  };

  const requestValue = (): string | number | boolean | null => {
    if (editing === null) return null;
    if (['ROOKIE', 'SERIAL_NUMBERED', 'AUTOGRAPH', 'MEMORABILIA'].includes(editing)) {
      return editBoolean;
    }
    if (editing === 'PRICE') return usdInputToCents(editValue);
    return editValue.trim();
  };

  const runBulk = async (apply: boolean) => {
    if (
      editing === null ||
      busy !== null ||
      (apply && (!preview?.ok || preview.data.summary.updated === 0))
    )
      return;
    const sourceEdit = ['SET', 'SUBSET', 'PARALLEL'].includes(editing);
    if (
      apply &&
      sourceEdit &&
      (!identityChangesAccepted ||
        !preview?.ok ||
        !validGridSourcePreview(
          preview.data,
          batchId,
          editing,
          cards.map((card) => card.cardId),
        ))
    ) {
      setError(
        'Refresh required. Preview the current per-card identity changes and accept them before applying.',
      );
      return;
    }
    const value = requestValue();
    if (value === null || value === '') {
      setError('Enter or select a value.');
      return;
    }
    if ((editing === 'SUBSET' || editing === 'PARALLEL') && editorChecklistId === '') {
      setError('Choose the imported checklist that supplies these values.');
      return;
    }
    const token = ++requestSequence.current;
    setBusy(apply ? 'APPLY' : 'PREVIEW');
    setError(null);
    setNotice(null);
    let receivedApply = false;
    try {
      const result = await catalog.bulkEditBatch(batchId, {
        preview: !apply,
        ...(sourceEdit
          ? {
              previewVersion: 2 as const,
              ...(apply && preview?.ok
                ? {
                    expectedContextPreviewDigest: preview.data.contextPreviewDigest,
                    expectedProposalDigests: Object.fromEntries(
                      preview.data.outcomes
                        .filter(
                          (row) =>
                            row.checklistAssigned === true &&
                            ['UPDATED', 'UNCHANGED'].includes(row.status),
                        )
                        .map((row) => [row.cardId, row.proposalDigest!]),
                    ),
                  }
                : {}),
            }
          : {}),
        field: editing,
        value,
        ...(editing === 'PRICE' ? { mode: priceMode } : {}),
        ...(editing === 'SUBSET' || editing === 'PARALLEL'
          ? { checklistId: editorChecklistId }
          : {}),
      });
      if (token !== requestSequence.current) return;
      if (!result.ok) {
        if (apply && sourceEdit) {
          setPreview(null);
          setIdentityChangesAccepted(false);
        }
        setError(result.message ?? result.error);
        return;
      }
      if (!apply) {
        if (
          sourceEdit &&
          !validGridSourcePreview(
            result.data,
            batchId,
            editing,
            cards.map((card) => card.cardId),
          )
        ) {
          setPreview(null);
          setIdentityChangesAccepted(false);
          setError(
            'Refresh required. The server did not return complete supported per-card identity changes and freshness checks. No changes were applied.',
          );
          return;
        }
        setIdentityChangesAccepted(false);
        setPreview(result);
        return;
      }
      receivedApply = true;
      setAppliedResult(result.data);
      if (
        sourceEdit &&
        (result.data.outcomes.some((row) => row.status === 'UNKNOWN') ||
          result.data.contextUpdate === 'UNKNOWN' ||
          result.data.contextUpdate === 'CONFLICT' ||
          result.data.previewVersion !== 2 ||
          !['UPDATED', 'UNCHANGED', 'CONFLICT', 'UNKNOWN'].includes(
            result.data.contextUpdate ?? '',
          ))
      ) {
        setPreview(null);
        setIdentityChangesAccepted(false);
        setError(
          'Some card or batch-context outcomes need review. Successful card outcomes remain saved. Refresh and preview again; do not automatically repeat Apply.',
        );
        await onUpdated();
        return;
      }
      setNotice(
        commonDraftSummary(result.data) ??
          (result.data.summary.assigned !== undefined
            ? assignmentSummary(result.data.summary)
            : `${result.data.summary.updated} updated, ${result.data.summary.unchanged} unchanged, ${result.data.summary.unresolved} unresolved, ${result.data.summary.failed} failed.`),
      );
      await onUpdated();
      if (token !== requestSequence.current) return;
      setPreview(null);
      setEditing(null);
    } catch (caught) {
      if (token === requestSequence.current) {
        if (apply && sourceEdit) {
          setPreview(null);
          setIdentityChangesAccepted(false);
          setError(
            receivedApply
              ? `Card outcomes were returned, but reload failed: ${caught instanceof Error ? caught.message : 'read unavailable'}. Refresh and preview again; saved outcomes remain shown.`
              : 'The Apply outcome could not be confirmed. Refresh and preview again; do not repeat the old Apply.',
          );
        } else setError(caught instanceof Error ? caught.message : 'Bulk edit failed.');
      }
    } finally {
      if (token === requestSequence.current) setBusy(null);
    }
  };

  useEffect(() => {
    if (busy === 'APPLY') return;
    requestSequence.current += 1;
    setPreview(null);
    setIdentityChangesAccepted(false);
    setBusy(null);
    // Card refresh invalidates displayed preimages; the active Apply owns its explicit result/reload.
  }, [cards]);
  const sourceEdit = editing !== null && ['SET', 'SUBSET', 'PARALLEL'].includes(editing);
  const summary = preview?.ok ? preview.data.summary : null;
  const attention = (result: BatchGridBulkEditResponse) => (
    <details open={result.summary.assigned !== undefined || commonDraftSummary(result) !== null}>
      <summary>Cards requiring attention ({result.outcomes.filter(needsAttention).length})</summary>
      <ul className="tcvs-batch-grid-outcomes">
        {result.outcomes.filter(needsAttention).map((item) => {
          const card = cards.find((candidate) => candidate.cardId === item.cardId);
          return (
            <li key={item.cardId}>
              <button
                type="button"
                className="tcvs-link-button tcvs-mono"
                onClick={() => onOpenCard(item.cardId)}
              >
                {card?.shortCode ?? item.cardId}
              </button>
              {' · '}
              {item.checklistAssigned && item.identityResolution === 'UNRESOLVED'
                ? 'Assigned; pending identification. '
                : item.reason === 'parallel_selection_required'
                  ? item.status === 'UNCHANGED'
                    ? 'Draft unchanged. '
                    : result.preview
                      ? 'Draft will update. '
                      : 'Draft updated. '
                  : item.checklistAssigned && item.identityResolution === 'UNRESOLVED'
                    ? 'Assigned; pending identification. '
                    : ''}
              {batchGridOutcomeReason(item.reason)}
            </li>
          );
        })}
      </ul>
    </details>
  );

  return (
    <section className="tcvs-batch-grid" aria-label="Batch grid">
      <div className="tcvs-batch-grid-toolbar">
        <div className="tcvs-batch-grid-presets" aria-label="Column preset">
          {(['IDENTIFICATION', 'LISTING', 'INVENTORY', 'ALL'] as const).map((item) => (
            <button
              type="button"
              key={item}
              aria-pressed={preset === item}
              onClick={() => setPreset(item)}
            >
              {item === 'ALL' ? 'All attributes' : item[0] + item.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
        <label className="tcvs-batch-grid-filter">
          <span className="tcvs-sr-only">Filter batch cards</span>
          <input
            type="search"
            placeholder="Filter cards in this batch"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
        </label>
        <label className="tcvs-batch-grid-sort">
          <span>Sort</span>
          <select value={sort} onChange={(event) => setSort(event.currentTarget.value as GridSort)}>
            <option value="POSITION">Batch position</option>
            <option value="CARD">Card ID</option>
            <option value="NUMBER">Card number</option>
            <option value="NAME">Name</option>
            <option value="PRICE">Price</option>
          </select>
          <button type="button" onClick={() => setDescending((current) => !current)}>
            {descending ? 'Descending' : 'Ascending'}
          </button>
        </label>
        <span className="tcvs-muted">{rows.length} cards</span>
      </div>

      {notice !== null ? (
        <p className="tcvs-workbench-notice" role="status">
          {notice}
        </p>
      ) : null}
      {appliedResult?.outcomes.some(needsAttention) ? attention(appliedResult) : null}
      {error !== null && editing === null ? <p className="tcvs-error">{error}</p> : null}
      <div className="tcvs-batch-grid-scroll">
        <table>
          <thead>
            <tr>
              <th className="tcvs-batch-grid-sticky tcvs-batch-grid-images">Images</th>
              <th className="tcvs-batch-grid-sticky tcvs-batch-grid-id">Card</th>
              <th>Status</th>
              <th>Ownership</th>
              {showPrice ? (
                <th>
                  <EditableHeader field="PRICE" onEdit={openEditor} />
                </th>
              ) : null}
              {show('IDENTITY') ? (
                <th>
                  <EditableHeader field="SET" onEdit={openEditor} />
                </th>
              ) : null}
              {show('IDENTITY') ? (
                <th>
                  <EditableHeader field="SUBSET" onEdit={openEditor} />
                </th>
              ) : null}
              {show('IDENTITY') ? (
                <th>
                  <EditableHeader field="PARALLEL" onEdit={openEditor} />
                </th>
              ) : null}
              {show('IDENTITY') ? <th>Card #</th> : null}
              {show('IDENTITY') ? <th>Name</th> : null}
              {show('IDENTITY') ? <th>Team</th> : null}
              {show('IDENTITY') ? (
                <th>
                  <EditableHeader field="ROOKIE" onEdit={openEditor} />
                </th>
              ) : null}
              {show('IDENTITY') ? (
                <th>
                  <EditableHeader field="SERIAL_NUMBERED" onEdit={openEditor} />
                </th>
              ) : null}
              {show('IDENTITY') ? (
                <th>
                  <EditableHeader field="AUTOGRAPH" onEdit={openEditor} />
                </th>
              ) : null}
              {show('IDENTITY') ? (
                <th>
                  <EditableHeader field="MEMORABILIA" onEdit={openEditor} />
                </th>
              ) : null}
              {show('LISTING') ? <th>SKU</th> : null}
              {show('INVENTORY') ? <th>Short ID</th> : null}
              {show('INVENTORY') ? <th>SKU</th> : null}
              {show('INVENTORY') ? <th>Image status</th> : null}
              {show('INVENTORY') ? <th>Storage batch</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((card) => {
              const identity = identityForGrid(card);
              return (
                <tr key={card.cardId}>
                  <td className="tcvs-batch-grid-sticky tcvs-batch-grid-images">
                    <button
                      type="button"
                      onClick={() => onOpenCard(card.cardId)}
                      title="Open card review"
                    >
                      {(['front', 'back'] as const).map((side) =>
                        card.imageUrls?.[side] === undefined ? null : (
                          <img
                            key={side}
                            src={card.imageUrls[side]}
                            alt={`${side} of ${card.shortCode ?? card.cardId}`}
                          />
                        ),
                      )}
                    </button>
                  </td>
                  <td className="tcvs-batch-grid-sticky tcvs-batch-grid-id">
                    <button
                      type="button"
                      className="tcvs-linklike"
                      onClick={() => onOpenCard(card.cardId)}
                    >
                      {card.shortCode ?? card.cardId}
                    </button>
                    <div className="tcvs-muted">
                      Position {card.currentBatchPosition ?? '\u2014'}
                    </div>
                  </td>
                  <td>
                    {reviewState(card)}
                    {card.reviewChecklistSelection?.requiresReview &&
                    card.reviewChecklistSelection.identityResolution === 'UNRESOLVED' ? (
                      <div className="tcvs-muted">
                        {batchGridOutcomeReason(card.reviewChecklistSelection.reason)}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <OwnershipLabel label={ownership[card.cardId]} />
                  </td>
                  {showPrice ? <td>{dollars(card)}</td> : null}
                  {show('IDENTITY') ? <td>{assignedSetLabel(card, checklists)}</td> : null}
                  {show('IDENTITY') ? (
                    <td>
                      {card.reviewChecklistSelection?.subset?.name ??
                        (card.reviewChecklistSelection?.requiresReview
                          ? card.reviewChecklistSelection.reason === 'parallel_selection_required'
                            ? card.identityReviewDraft?.identity.subset || '—'
                            : '—'
                          : identity?.subset || '—')}
                    </td>
                  ) : null}
                  {show('IDENTITY') ? (
                    <td>
                      {card.reviewChecklistSelection?.parallel?.kind === 'NONE'
                        ? 'No parallel / Base'
                        : card.reviewChecklistSelection?.parallel?.kind === 'DEFINITION'
                          ? card.reviewChecklistSelection.parallel.name
                          : card.reviewChecklistSelection?.requiresReview
                            ? '—'
                            : identity?.variation || 'No parallel / Base'}
                    </td>
                  ) : null}
                  {show('IDENTITY') ? <td>{identity?.number || '—'}</td> : null}
                  {show('IDENTITY') ? <td>{identity?.name || '—'}</td> : null}
                  {show('IDENTITY') ? <td>{identity?.team || '—'}</td> : null}
                  {show('IDENTITY') ? <td>{boolLabel(identity?.rookie)}</td> : null}
                  {show('IDENTITY') ? <td>{serialLabel(identity)}</td> : null}
                  {show('IDENTITY') ? <td>{boolLabel(identity?.autograph)}</td> : null}
                  {show('IDENTITY') ? <td>{boolLabel(identity?.memorabilia)}</td> : null}
                  {show('LISTING') ? <td>{card.sku ?? '—'}</td> : null}
                  {show('INVENTORY') ? <td>{card.shortCode ?? '—'}</td> : null}
                  {show('INVENTORY') ? <td>{card.sku ?? '—'}</td> : null}
                  {show('INVENTORY') ? <td>{card.imageQualityStatus ?? 'PENDING'}</td> : null}
                  {show('INVENTORY') ? <td>{card.placementBatchId ?? '—'}</td> : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing !== null ? (
        <div className="tcvs-modal-backdrop" role="presentation">
          <section
            className="tcvs-modal tcvs-batch-grid-editor"
            role="dialog"
            aria-modal="true"
            aria-label={`Bulk edit ${fieldLabel(editing)}`}
          >
            <header>
              <div>
                <h3>Bulk edit {fieldLabel(editing)}</h3>
                <p className="tcvs-muted">
                  {editing === 'SET' || editing === 'SUBSET' || editing === 'PARALLEL'
                    ? `Checks all ${cards.length} active cards in this batch and assigns the selected ${fieldLabel(editing).toLowerCase()} to eligible cards, including cards hidden by filters. Exact matches get a draft; other assigned cards need identification.`
                    : `Applies to all ${cards.length} active cards in this batch.`}
                </p>
              </div>
              <button
                type="button"
                className="tcvs-icon-button"
                aria-label="Close bulk editor"
                disabled={busy === 'APPLY'}
                onClick={() => {
                  invalidatePreview();
                  setBusy(null);
                  setEditing(null);
                }}
              >
                ×
              </button>
            </header>

            {editing === 'SUBSET' || editing === 'PARALLEL' ? (
              <label className="tcvs-field">
                <span>Checklist source</span>
                <FilterableSelect
                  value={editorChecklistId}
                  disabled={busy === 'APPLY'}
                  options={checklists.map((item) => ({
                    value: item.checklistId,
                    label: `${item.product.year} ${item.product.product} · ${item.category}`,
                    searchText: `${item.product.brand} ${item.source?.targetId ?? ''}`,
                  }))}
                  onChange={(value) => {
                    setEditorChecklistId(value);
                    setEditValue('');
                    invalidatePreview();
                    setBusy(null);
                  }}
                  placeholder="Search imported sets"
                  ariaLabel="Checklist source for bulk edit"
                  loading={checklistSearchLoading}
                  searchError={checklistSearchError}
                  hasMore={checklistSearchHasMore}
                  onLoadMore={onChecklistLoadMore}
                  onRetry={onChecklistRetry}
                  onQueryChange={onChecklistSearch}
                />
              </label>
            ) : null}

            {editing === 'SET' ? (
              <FilterableSelect
                value={editValue}
                disabled={busy === 'APPLY'}
                options={checklists.map((item) => ({
                  value: item.checklistId,
                  label: `${item.product.year} ${item.product.product} · ${item.category}`,
                  searchText: `${item.product.brand} ${item.source?.targetId ?? ''}`,
                }))}
                onChange={(value) => {
                  setEditValue(value);
                  invalidatePreview();
                  setBusy(null);
                }}
                placeholder="Search imported sets"
                ariaLabel="Set for every card"
                loading={checklistSearchLoading}
                searchError={checklistSearchError}
                hasMore={checklistSearchHasMore}
                onLoadMore={onChecklistLoadMore}
                onRetry={onChecklistRetry}
                onQueryChange={onChecklistSearch}
              />
            ) : editing === 'PRICE' ? (
              <>
                <label className="tcvs-field">
                  <span>Price (USD)</span>
                  <input
                    inputMode="decimal"
                    disabled={busy === 'APPLY'}
                    placeholder="0.00"
                    value={editValue}
                    onChange={(event) => {
                      setEditValue(event.currentTarget.value);
                      invalidatePreview();
                      setBusy(null);
                    }}
                  />
                </label>
                <div className="tcvs-segmented-control" aria-label="Price application mode">
                  <button
                    type="button"
                    disabled={busy === 'APPLY'}
                    aria-pressed={priceMode === 'REPLACE'}
                    onClick={() => {
                      setPriceMode('REPLACE');
                      invalidatePreview();
                      setBusy(null);
                    }}
                  >
                    Replace all
                  </button>
                  <button
                    type="button"
                    disabled={busy === 'APPLY'}
                    aria-pressed={priceMode === 'FILL_BLANKS'}
                    onClick={() => {
                      setPriceMode('FILL_BLANKS');
                      invalidatePreview();
                      setBusy(null);
                    }}
                  >
                    Fill blanks only
                  </button>
                </div>
              </>
            ) : ['ROOKIE', 'SERIAL_NUMBERED', 'AUTOGRAPH', 'MEMORABILIA'].includes(editing) ? (
              <div className="tcvs-segmented-control" aria-label={`${fieldLabel(editing)} value`}>
                <button
                  type="button"
                  disabled={busy === 'APPLY'}
                  aria-pressed={editBoolean}
                  onClick={() => {
                    setEditBoolean(true);
                    invalidatePreview();
                    setBusy(null);
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  disabled={busy === 'APPLY'}
                  aria-pressed={!editBoolean}
                  onClick={() => {
                    setEditBoolean(false);
                    invalidatePreview();
                    setBusy(null);
                  }}
                >
                  No
                </button>
              </div>
            ) : (
              <>
                <label className="tcvs-field">
                  <span>{fieldLabel(editing)}</span>
                  <FilterableSelect
                    value={editValue}
                    options={editing === 'PARALLEL' ? parallelOptions : subsetOptions}
                    onChange={(value) => {
                      setEditValue(value);
                      invalidatePreview();
                      setBusy(null);
                    }}
                    placeholder={
                      sourceOptionsBusy
                        ? `Loading ${fieldLabel(editing).toLocaleLowerCase('en-US')} values…`
                        : `Search imported ${fieldLabel(editing).toLocaleLowerCase('en-US')} values`
                    }
                    disabled={busy === 'APPLY' || editorChecklistId === '' || sourceOptionsBusy}
                    ariaLabel={`${fieldLabel(editing)} for every card`}
                  />
                </label>
                {!sourceOptionsBusy &&
                editorChecklistId !== '' &&
                (editing === 'PARALLEL' ? parallelDefinitions.length : subsetDefinitions.length) ===
                  0 ? (
                  <p className="tcvs-muted">
                    No imported {fieldLabel(editing).toLocaleLowerCase('en-US')} values were found
                    for this checklist.
                  </p>
                ) : null}
              </>
            )}

            {error !== null ? <p className="tcvs-error">{error}</p> : null}
            {summary !== null ? (
              <div className="tcvs-batch-grid-preview">
                <strong>Preview</strong>
                {preview?.ok && commonDraftSummary(preview.data) !== null ? (
                  <span>{commonDraftSummary(preview.data)}</span>
                ) : summary.assigned !== undefined ? (
                  <span>{assignmentSummary(summary, true)}</span>
                ) : (
                  <>
                    <span>{summary.updated} will update</span>
                    <span>{summary.unchanged} unchanged</span>
                    <span>{summary.unresolved} unresolved</span>
                    <span>{summary.failed} failed</span>
                  </>
                )}
              </div>
            ) : null}
            {preview?.ok && preview.data.outcomes.some(needsAttention)
              ? attention(preview.data)
              : null}

            {sourceEdit && preview?.ok ? (
              <section aria-label="Per-card source changes">
                {preview.data.outcomes
                  .filter((row) => row.identityProposal !== undefined)
                  .map((row) => (
                    <article key={row.cardId}>
                      <h4>
                        {cards.find((card) => card.cardId === row.cardId)?.shortCode ?? row.cardId}
                      </h4>
                      <ChecklistSelectionChanges
                        beforeCard={row.identityProposal!.beforeCardIdentity}
                        beforeDraft={row.identityProposal!.beforeDraftIdentity}
                        proposed={row.identityProposal!.proposedIdentity}
                      />
                    </article>
                  ))}
                <label>
                  <input
                    type="checkbox"
                    checked={identityChangesAccepted}
                    disabled={busy !== null}
                    onChange={(event) => setIdentityChangesAccepted(event.target.checked)}
                  />
                  I reviewed every displayed card and draft identity, including removed serial
                  numbers and other facts, and accept these exact per-card changes.
                </label>
              </section>
            ) : null}
            <footer>
              <button
                type="button"
                onClick={() => void runBulk(false)}
                disabled={busy !== null || sourceOptionsBusy}
              >
                {busy === 'PREVIEW' ? 'Previewing…' : 'Preview changes'}
              </button>
              <button
                type="button"
                onClick={() => void runBulk(true)}
                disabled={
                  busy !== null ||
                  summary === null ||
                  summary.updated === 0 ||
                  (sourceEdit && !identityChangesAccepted)
                }
              >
                {busy === 'APPLY' ? 'Applying…' : `Update ${summary?.updated ?? 0} cards`}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </section>
  );
}
